import { Download, Plus, RotateCcw, Save, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import AdminLogin from '../../components/admin/AdminLogin';
import MediaUploader from '../../components/admin/MediaUploader';
import AdminUnauthorized from '../../components/admin/AdminUnauthorized';
import Layout from '../../components/Layout';
import SectionHeading from '../../components/SectionHeading';
import { useSiteContent } from '../../content/ContentContext';
import { defaultSiteContent, type SiteContent } from '../../data/site';
import { uploadCmsImage } from '../../lib/cms';
import { adminEmailAllowlist, isAllowedAdminEmail, isSupabaseConfigured, supabase } from '../../lib/supabase';

const sectionTabs = [
  { id: 'business', label: 'Business' },
  { id: 'home', label: 'Homepage' },
  { id: 'services', label: 'Services' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'about', label: 'About' },
  { id: 'media', label: 'Media' },
] as const;

type TabId = (typeof sectionTabs)[number]['id'];

type TextListEditorProps = {
  label: string;
  values: string[];
  onChange: (nextValues: string[]) => void;
  addLabel: string;
};

type Card = {
  title: string;
  text: string;
};

type CardListEditorProps = {
  label: string;
  values: Card[];
  onChange: (nextValues: Card[]) => void;
  addLabel: string;
};

function Field({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
}) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={4}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm font-normal outline-none transition focus:border-[#33b300]"
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm font-normal outline-none transition focus:border-[#33b300]"
        />
      )}
    </label>
  );
}

function TextListEditor({ label, values, onChange, addLabel }: TextListEditorProps) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
        <button
          type="button"
          onClick={() => onChange([...values, ''])}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
        >
          <Plus size={16} /> {addLabel}
        </button>
      </div>
      <div className="space-y-3">
        {values.map((value, index) => (
          <div key={`${label}-${index}`} className="flex gap-3">
            <input
              value={value}
              onChange={(event) => {
                const nextValues = [...values];
                nextValues[index] = event.target.value;
                onChange(nextValues);
              }}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#33b300]"
            />
            <button
              type="button"
              onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-200 text-red-600 transition hover:bg-red-50"
              aria-label={`Remove ${label} item ${index + 1}`}
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardListEditor({ label, values, onChange, addLabel }: CardListEditorProps) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
        <button
          type="button"
          onClick={() => onChange([...values, { title: '', text: '' }])}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
        >
          <Plus size={16} /> {addLabel}
        </button>
      </div>
      <div className="space-y-4">
        {values.map((value, index) => (
          <div key={`${label}-${index}`} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
              >
                <Trash2 size={16} /> Remove
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label="Title"
                value={value.title}
                onChange={(nextValue) => {
                  const nextValues = [...values];
                  nextValues[index] = { ...nextValues[index], title: nextValue };
                  onChange(nextValues);
                }}
              />
              <Field
                label="Description"
                value={value.text}
                multiline
                onChange={(nextValue) => {
                  const nextValues = [...values];
                  nextValues[index] = { ...nextValues[index], text: nextValue };
                  onChange(nextValues);
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { content, saveContent, resetContent, contentSource, isLoadingContent } = useSiteContent();
  const [draft, setDraft] = useState<SiteContent>(content);
  const [activeTab, setActiveTab] = useState<TabId>('business');
  const [statusMessage, setStatusMessage] = useState('Changes are only saved here after you click Save CMS Content.');
  const [isSaving, setIsSaving] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(Boolean(isSupabaseConfigured));
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState('');
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(content);
  }, [content]);

  useEffect(() => {
    if (!supabase) {
      setIsAuthLoading(false);
      setIsSignedIn(true);
      setIsUnauthorized(false);
      return;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) {
        return;
      }

      const email = data.session?.user?.email || '';
      const isAllowed = isAllowedAdminEmail(email);

      if (data.session && !isAllowed) {
        setIsUnauthorized(true);
      } else {
        setIsUnauthorized(false);
      }

      setSignedInEmail(email);
      setIsSignedIn(Boolean(data.session) && isAllowed);
      setAuthError(
        data.session && !isAllowed
          ? 'This account is not approved for admin access.'
          : '',
      );
      setIsAuthLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const email = session?.user?.email || '';
      const isAllowed = isAllowedAdminEmail(email);

      if (session && !isAllowed) {
        setIsUnauthorized(true);
      } else {
        setIsUnauthorized(false);
      }

      setSignedInEmail(email);
      setIsSignedIn(Boolean(session) && isAllowed);
      setAuthError(session && !isAllowed ? 'This account is not approved for admin access.' : '');
      setIsAuthLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const updateContent = (updater: (current: SiteContent) => SiteContent) => {
    setDraft((current) => updater(current));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await saveContent(draft);
      setStatusMessage(
        isSupabaseConfigured
          ? 'CMS content saved to Supabase and synced locally.'
          : 'CMS content saved in this browser.',
      );
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'Saving failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      setDraft(defaultSiteContent);
      await resetContent();
      setStatusMessage(
        isSupabaseConfigured
          ? 'CMS content reset and synced to Supabase.'
          : 'CMS content reset to the original website copy.',
      );
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'Reset failed.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bbsite-content.json';
    link.click();
    URL.revokeObjectURL(url);
    setStatusMessage('Content exported as bbsite-content.json.');
  };

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      const imported = JSON.parse(await file.text()) as SiteContent;
      setDraft(imported);
      await saveContent(imported);
      setStatusMessage(
        isSupabaseConfigured ? 'Content imported and synced to Supabase.' : 'Content imported and saved.',
      );
    } catch {
      setStatusMessage('Import failed. Please choose a valid CMS JSON file.');
    } finally {
      event.target.value = '';
    }
  };

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    if (!supabase) {
      return;
    }

    try {
      setIsSigningIn(true);
      setAuthError('');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        throw error;
      }

      if (!isAllowedAdminEmail(data.user?.email)) {
        setSignedInEmail(data.user?.email || email);
        setIsUnauthorized(true);
        setIsSignedIn(false);
        await supabase.auth.signOut();
        throw new Error('This email is not on the approved admin allowlist.');
      }
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Sign-in failed.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    if (!supabase) {
      return;
    }

    await supabase.auth.signOut();
    setIsUnauthorized(false);
    setSignedInEmail('');
    setAuthError('');
    setStatusMessage('Signed out of the CMS.');
  };

  const handleImageUpload = async (file: File) => {
    const uploaded = await uploadCmsImage(file);
    setStatusMessage('Image uploaded to Supabase Storage.');
    return uploaded.publicUrl;
  };

  const handleAddUploadedImageToGallery = (image: { src: string; alt: string }) => {
    updateContent((current) => ({
      ...current,
      galleryImages: [...current.galleryImages, image],
    }));
    setActiveTab('gallery');
    setStatusMessage('Image added to the gallery draft. Click Save CMS Content to publish it.');
  };

  return (
    <Layout>
      <section className="bg-[#123018] text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-[#7dff45]">Customer CMS</p>
          <h1 className="max-w-4xl text-4xl font-black tracking-tight sm:text-5xl">Edit the website without touching code</h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-white/85">
            This admin page controls the text, services, gallery, and reviews shown across the site. Save changes here, then export a backup JSON any time.
          </p>
        </div>
      </section>

      {isAuthLoading || isLoadingContent ? (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-700 shadow-sm">
            Loading CMS...
          </div>
        </section>
      ) : null}

      {!isAuthLoading && !isSignedIn ? (
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          {isUnauthorized ? (
            <AdminUnauthorized email={signedInEmail} onSignOut={handleSignOut} />
          ) : (
            <AdminLogin onSubmit={handleLogin} isSubmitting={isSigningIn} errorMessage={authError} />
          )}
        </section>
      ) : null}

      {!isAuthLoading && isSignedIn ? (
        <>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#33b300]">CMS Actions</p>
              <p className="mt-2 text-sm text-slate-600">{statusMessage}</p>
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Content source: {contentSource}
              </p>
              {signedInEmail ? (
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Signed in as: {signedInEmail}
                </p>
              ) : null}
              {adminEmailAllowlist.length > 0 ? (
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Admin allowlist active
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-full bg-[#0d4f12] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0a3c0e]"
              >
                <Save size={18} /> {isSaving ? 'Saving...' : 'Save CMS Content'}
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
              >
                <Download size={18} /> Export JSON
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
              >
                <Upload size={18} /> Import JSON
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 px-5 py-3 text-sm font-bold text-red-600 transition hover:bg-red-50"
              >
                <RotateCcw size={18} /> Reset to Default
              </button>
              {isSupabaseConfigured ? (
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
                >
                  Sign Out
                </button>
              ) : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                onChange={handleImport}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 pb-16 sm:px-6 lg:grid-cols-[260px_1fr] lg:px-8 lg:pb-24">
        <aside className="h-fit rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="grid gap-2">
            {sectionTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-2xl px-4 py-3 text-left text-sm font-semibold transition ${
                  activeTab === tab.id ? 'bg-[#eff9e8] text-[#0d4f12]' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="space-y-8">
          {activeTab === 'business' ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeading eyebrow="Business Details" title="Global site settings" description="These fields affect the header, footer, contact section, and key business info across the website." />
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Business name" value={draft.site.businessName} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, businessName: value } }))} />
                <Field label="Short business name" value={draft.site.shortBusinessName} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, shortBusinessName: value } }))} />
                <Field label="Phone display" value={draft.site.phoneDisplay} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, phoneDisplay: value } }))} />
                <Field label="Phone link" value={draft.site.phoneHref} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, phoneHref: value } }))} />
                <Field label="Email" value={draft.site.email} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, email: value } }))} />
                <Field label="Service area" value={draft.site.serviceArea} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, serviceArea: value } }))} />
                <Field label="Property types" value={draft.site.propertyTypes} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, propertyTypes: value } }))} />
                <Field label="Form endpoint" value={draft.site.formspree} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, formspree: value } }))} />
                <Field label="Facebook reviews URL" value={draft.site.facebookReviews} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, facebookReviews: value } }))} />
                <Field label="Copyright year" value={draft.site.copyrightYear} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, copyrightYear: value } }))} />
                <div className="md:col-span-2">
                  <Field label="Top bar text" value={draft.site.topBarText} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, topBarText: value } }))} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Footer description" value={draft.site.footerDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, footerDescription: value } }))} />
                </div>
              </div>
              <div className="mt-6">
                <TextListEditor
                  label="Service area badges"
                  values={draft.site.serviceAreaShortList}
                  onChange={(values) => updateContent((current) => ({ ...current, site: { ...current.site, serviceAreaShortList: values } }))}
                  addLabel="Add area"
                />
              </div>
            </div>
          ) : null}

          {activeTab === 'home' ? (
            <>
              <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeading eyebrow="Homepage Hero" title="Main first impression" description="Control the hero copy and homepage feature sections." />
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Hero eyebrow" value={draft.site.heroEyebrow} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, heroEyebrow: value } }))} />
                  <Field label="Hero image path" value={draft.site.heroImage} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, heroImage: value } }))} />
                  <Field label="Hero title" value={draft.site.heroTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, heroTitle: value } }))} />
                  <Field label="Hero image alt text" value={draft.site.heroImageAlt} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, heroImageAlt: value } }))} />
                  <div className="md:col-span-2">
                    <Field label="Hero description" value={draft.site.heroDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, heroDescription: value } }))} />
                  </div>
                </div>
              </div>

              <TextListEditor
                label="Trust points"
                values={draft.site.trustPoints}
                onChange={(values) => updateContent((current) => ({ ...current, site: { ...current.site, trustPoints: values } }))}
                addLabel="Add point"
              />

              <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeading eyebrow="Homepage Sections" title="Promotional content" description="These blocks power the supporting sections on the homepage." />
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="Why choose title" value={draft.site.whyChooseTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, whyChooseTitle: value } }))} />
                  <Field label="Why choose image path" value={draft.site.whyChooseImage} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, whyChooseImage: value } }))} />
                  <Field label="Recent work title" value={draft.site.recentWorkTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, recentWorkTitle: value } }))} />
                  <Field label="Why choose image alt text" value={draft.site.whyChooseImageAlt} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, whyChooseImageAlt: value } }))} />
                  <Field label="Reviews title" value={draft.site.reviewsTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, reviewsTitle: value } }))} />
                  <Field label="Service area title" value={draft.site.serviceAreaTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, serviceAreaTitle: value } }))} />
                  <Field label="Free estimate title" value={draft.site.freeEstimateTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, freeEstimateTitle: value } }))} />
                  <div className="md:col-span-2">
                    <Field label="Why choose description" value={draft.site.whyChooseDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, whyChooseDescription: value } }))} />
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Recent work description" value={draft.site.recentWorkDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, recentWorkDescription: value } }))} />
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Reviews description" value={draft.site.reviewsDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, reviewsDescription: value } }))} />
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Service area description" value={draft.site.serviceAreaDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, serviceAreaDescription: value } }))} />
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Free estimate description" value={draft.site.freeEstimateDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, freeEstimateDescription: value } }))} />
                  </div>
                </div>
              </div>

              <TextListEditor
                label="Why choose points"
                values={draft.site.whyChoosePoints}
                onChange={(values) => updateContent((current) => ({ ...current, site: { ...current.site, whyChoosePoints: values } }))}
                addLabel="Add point"
              />
            </>
          ) : null}

          {activeTab === 'services' ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeading eyebrow="Services" title="Service groups and cards" description="Edit the service page and the homepage featured services source list." />
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Services page title" value={draft.site.servicesPageTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, servicesPageTitle: value } }))} />
                <div className="md:col-span-2">
                  <Field label="Services page description" value={draft.site.servicesPageDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, servicesPageDescription: value } }))} />
                </div>
              </div>
              <div className="mt-6 space-y-6">
                {draft.serviceGroups.map((group, groupIndex) => (
                  <div key={`${group.title}-${groupIndex}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <Field
                          label={`Group ${groupIndex + 1} title`}
                          value={group.title}
                          onChange={(value) =>
                            updateContent((current) => {
                              const nextGroups = [...current.serviceGroups];
                              nextGroups[groupIndex] = { ...nextGroups[groupIndex], title: value };
                              return { ...current, serviceGroups: nextGroups };
                            })
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateContent((current) => ({
                            ...current,
                            serviceGroups: current.serviceGroups.filter((_, index) => index !== groupIndex),
                          }))
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={16} /> Remove group
                      </button>
                    </div>
                    <div className="space-y-4">
                      {group.items.map((item, itemIndex) => (
                        <div key={`${item.name}-${itemIndex}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="mb-3 flex justify-end">
                            <button
                              type="button"
                              onClick={() =>
                                updateContent((current) => {
                                  const nextGroups = [...current.serviceGroups];
                                  nextGroups[groupIndex] = {
                                    ...nextGroups[groupIndex],
                                    items: nextGroups[groupIndex].items.filter((_, index) => index !== itemIndex),
                                  };
                                  return { ...current, serviceGroups: nextGroups };
                                })
                              }
                              className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={16} /> Remove service
                            </button>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <Field
                              label="Service name"
                              value={item.name}
                              onChange={(value) =>
                                updateContent((current) => {
                                  const nextGroups = [...current.serviceGroups];
                                  nextGroups[groupIndex].items[itemIndex] = {
                                    ...nextGroups[groupIndex].items[itemIndex],
                                    name: value,
                                  };
                                  return { ...current, serviceGroups: nextGroups };
                                })
                              }
                            />
                            <Field
                              label="Image path"
                              value={item.image}
                              onChange={(value) =>
                                updateContent((current) => {
                                  const nextGroups = [...current.serviceGroups];
                                  nextGroups[groupIndex].items[itemIndex] = {
                                    ...nextGroups[groupIndex].items[itemIndex],
                                    image: value,
                                  };
                                  return { ...current, serviceGroups: nextGroups };
                                })
                              }
                            />
                            <div className="md:col-span-2">
                              <Field
                                label="Description"
                                value={item.description}
                                multiline
                                onChange={(value) =>
                                  updateContent((current) => {
                                    const nextGroups = [...current.serviceGroups];
                                    nextGroups[groupIndex].items[itemIndex] = {
                                      ...nextGroups[groupIndex].items[itemIndex],
                                      description: value,
                                    };
                                    return { ...current, serviceGroups: nextGroups };
                                  })
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        updateContent((current) => {
                          const nextGroups = [...current.serviceGroups];
                          nextGroups[groupIndex] = {
                            ...nextGroups[groupIndex],
                            items: [...nextGroups[groupIndex].items, { name: '', description: '', image: '' }],
                          };
                          return { ...current, serviceGroups: nextGroups };
                        })
                      }
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
                    >
                      <Plus size={16} /> Add service
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateContent((current) => ({
                      ...current,
                      serviceGroups: [...current.serviceGroups, { title: '', items: [{ name: '', description: '', image: '' }] }],
                    }))
                  }
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
                >
                  <Plus size={16} /> Add service group
                </button>
              </div>
            </div>
          ) : null}

          {activeTab === 'gallery' ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeading eyebrow="Gallery" title="Manage gallery images" description="Enter image paths from the public folder and the alt text that describes each photo." />
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Gallery title" value={draft.site.galleryTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, galleryTitle: value } }))} />
                <div className="md:col-span-2">
                  <Field label="Gallery description" value={draft.site.galleryDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, galleryDescription: value } }))} />
                </div>
              </div>
              <div className="mt-6 space-y-4">
                {draft.galleryImages.map((image, index) => (
                  <div key={`${image.src}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          updateContent((current) => ({
                            ...current,
                            galleryImages: current.galleryImages.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={16} /> Remove image
                      </button>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Image path"
                        value={image.src}
                        onChange={(value) =>
                          updateContent((current) => {
                            const nextImages = [...current.galleryImages];
                            nextImages[index] = { ...nextImages[index], src: value };
                            return { ...current, galleryImages: nextImages };
                          })
                        }
                      />
                      <Field
                        label="Alt text"
                        value={image.alt}
                        onChange={(value) =>
                          updateContent((current) => {
                            const nextImages = [...current.galleryImages];
                            nextImages[index] = { ...nextImages[index], alt: value };
                            return { ...current, galleryImages: nextImages };
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  updateContent((current) => ({
                    ...current,
                    galleryImages: [...current.galleryImages, { src: '', alt: '' }],
                  }))
                }
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
              >
                <Plus size={16} /> Add gallery image
              </button>
            </div>
          ) : null}

          {activeTab === 'reviews' ? (
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <SectionHeading eyebrow="Reviews" title="Testimonials and reviews page copy" description="Manage customer review cards and the heading text for the reviews page." />
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Reviews page title" value={draft.site.reviewsPageTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, reviewsPageTitle: value } }))} />
                <div className="md:col-span-2">
                  <Field label="Reviews page description" value={draft.site.reviewsPageDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, reviewsPageDescription: value } }))} />
                </div>
              </div>
              <div className="mt-6 space-y-4">
                {draft.reviews.map((review, index) => (
                  <div key={`${review.name}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          updateContent((current) => ({
                            ...current,
                            reviews: current.reviews.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={16} /> Remove review
                      </button>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field
                        label="Customer name"
                        value={review.name}
                        onChange={(value) =>
                          updateContent((current) => {
                            const nextReviews = [...current.reviews];
                            nextReviews[index] = { ...nextReviews[index], name: value };
                            return { ...current, reviews: nextReviews };
                          })
                        }
                      />
                      <div className="md:col-span-2">
                        <Field
                          label="Review text"
                          value={review.text}
                          multiline
                          onChange={(value) =>
                            updateContent((current) => {
                              const nextReviews = [...current.reviews];
                              nextReviews[index] = { ...nextReviews[index], text: value };
                              return { ...current, reviews: nextReviews };
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  updateContent((current) => ({
                    ...current,
                    reviews: [...current.reviews, { name: '', text: '' }],
                  }))
                }
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
              >
                <Plus size={16} /> Add review
              </button>
            </div>
          ) : null}

          {activeTab === 'about' ? (
            <>
              <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeading eyebrow="About Page" title="Company story and supporting content" description="Update the about page copy, images, and values cards." />
                <div className="grid gap-5 md:grid-cols-2">
                  <Field label="About page title" value={draft.site.aboutTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, aboutTitle: value } }))} />
                  <Field label="Values title" value={draft.site.valuesTitle} onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, valuesTitle: value } }))} />
                  <div className="md:col-span-2">
                    <Field label="About page description" value={draft.site.aboutDescription} multiline onChange={(value) => updateContent((current) => ({ ...current, site: { ...current.site, aboutDescription: value } }))} />
                  </div>
                </div>
              </div>

              <TextListEditor
                label="About story paragraphs"
                values={draft.site.aboutStory}
                onChange={(values) => updateContent((current) => ({ ...current, site: { ...current.site, aboutStory: values } }))}
                addLabel="Add paragraph"
              />

              <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <SectionHeading eyebrow="About Images" title="Photo collage" description="Update the image paths and alt text used on the about page." />
                <div className="space-y-4">
                  {draft.site.aboutImages.map((image, index) => (
                    <div key={`${image.src}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            updateContent((current) => ({
                              ...current,
                              site: {
                                ...current.site,
                                aboutImages: current.site.aboutImages.filter((_, itemIndex) => itemIndex !== index),
                              },
                            }))
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                        >
                          <Trash2 size={16} /> Remove image
                        </button>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <Field
                          label="Image path"
                          value={image.src}
                          onChange={(value) =>
                            updateContent((current) => {
                              const nextImages = [...current.site.aboutImages];
                              nextImages[index] = { ...nextImages[index], src: value };
                              return { ...current, site: { ...current.site, aboutImages: nextImages } };
                            })
                          }
                        />
                        <Field
                          label="Alt text"
                          value={image.alt}
                          onChange={(value) =>
                            updateContent((current) => {
                              const nextImages = [...current.site.aboutImages];
                              nextImages[index] = { ...nextImages[index], alt: value };
                              return { ...current, site: { ...current.site, aboutImages: nextImages } };
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    updateContent((current) => ({
                      ...current,
                      site: {
                        ...current.site,
                        aboutImages: [...current.site.aboutImages, { src: '', alt: '' }],
                      },
                    }))
                  }
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]"
                >
                  <Plus size={16} /> Add about image
                </button>
              </div>

              <CardListEditor
                label="Values cards"
                values={draft.site.values}
                onChange={(values) => updateContent((current) => ({ ...current, site: { ...current.site, values } }))}
                addLabel="Add card"
              />
            </>
          ) : null}

          {activeTab === 'media' ? (
            <MediaUploader
              onUpload={handleImageUpload}
              onAddToGallery={handleAddUploadedImageToGallery}
              disabled={!isSupabaseConfigured}
            />
          ) : null}
        </div>
      </section>
        </>
      ) : null}
    </Layout>
  );
}
